import React, { useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, Eye, Download, Sparkles, TrendingUp, Loader2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { adminApi } from '@/lib/adminApi';
import { useDataLabel } from '@/lib/dataI18n';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

// Sprint A.1.3-γ — Resource Center Analytics Dashboard + Top-5 strip.

export function useResourceAnalytics(open = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    adminApi.resourceAnalyticsOverview()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => toast.error('Analytics unavailable', { description: formatApiError(e) }))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);
  return { data, loading };
}

export function ResourceAnalyticsDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const { data, loading } = useResourceAnalytics(open);
  const totals = data?.totals || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="resource-analytics-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            {t('resourceCenter.analytics.title', { defaultValue: 'Resource Analytics' })}
          </DialogTitle>
        </DialogHeader>
        {loading && (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        )}
        {!loading && data && (
          <div className="space-y-5">
            {/* Totals strip */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="analytics-totals">
              {[
                { id: 'resources', k: t('resourceCenter.analytics.resources', { defaultValue: 'Resources' }), v: totals.resources || 0, Icon: BarChart3 },
                { id: 'views',     k: t('resourceCenter.analytics.views', { defaultValue: 'Views' }),     v: totals.views || 0,     Icon: Eye },
                { id: 'previews',  k: t('resourceCenter.analytics.previews', { defaultValue: 'Previews' }),  v: totals.previews || 0,  Icon: Eye },
                { id: 'downloads', k: t('resourceCenter.analytics.downloads', { defaultValue: 'Downloads' }), v: totals.downloads || 0, Icon: Download },
                { id: 'imports',   k: t('resourceCenter.analytics.imports', { defaultValue: 'Imports' }),   v: totals.imports || 0,   Icon: Sparkles },
              ].map((c) => (
                <Card key={c.id} className="p-3" data-testid={`analytics-total-${c.id}`}>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                    <c.Icon className="h-3.5 w-3.5" /> {c.k}
                  </div>
                  <div className="text-2xl font-bold mt-1">{c.v}</div>
                </Card>
              ))}
            </div>

            {/* By category */}
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                {t('resourceCenter.analytics.byCategory', { defaultValue: 'By category' })}
              </div>
              <div className="overflow-x-auto" data-testid="analytics-by-category">
                <table className="min-w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left py-1.5 pr-2 font-semibold">{t('resourceCenter.analytics.category', { defaultValue: 'Category' })}</th>
                      <th className="text-right py-1.5 pr-2 font-semibold">{t('resourceCenter.analytics.resources', { defaultValue: 'Resources' })}</th>
                      <th className="text-right py-1.5 pr-2 font-semibold">{t('resourceCenter.analytics.views', { defaultValue: 'Views' })}</th>
                      <th className="text-right py-1.5 pr-2 font-semibold">{t('resourceCenter.analytics.previews', { defaultValue: 'Previews' })}</th>
                      <th className="text-right py-1.5 pr-2 font-semibold">{t('resourceCenter.analytics.downloads', { defaultValue: 'Downloads' })}</th>
                      <th className="text-right py-1.5 pr-2 font-semibold">{t('resourceCenter.analytics.imports', { defaultValue: 'Imports' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.by_category || []).map((row) => (
                      <tr key={row.category || 'unk'} className="border-t border-border/40">
                        <td className="py-1.5 pr-2 font-medium">{row.category ? t(`resourceCenter.categories.${row.category}`, { defaultValue: row.category }) : '—'}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{row.count}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{row.views}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{row.previews}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{row.downloads}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{row.imports}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top 5 */}
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                <TrendingUp className="h-3 w-3" /> {t('resourceCenter.analytics.topFive', { defaultValue: 'Top 5 most used this month' })}
              </div>
              {(data.top_5_this_month || []).length === 0 && (
                <p className="text-xs text-muted-foreground">{t('resourceCenter.analytics.noUsage', { defaultValue: 'No usage recorded yet.' })}</p>
              )}
              <ol className="space-y-1.5" data-testid="analytics-top-5">
                {(data.top_5_this_month || []).map((it, idx) => (
                  <li key={it.id} className="flex items-center gap-3 text-sm" data-testid={`analytics-top-row-${idx}`}>
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold">{idx + 1}</span>
                    <span className="flex-1 truncate font-medium">{tData(it.title)}</span>
                    <Badge variant="outline" className="text-xs">{t(`resourceCenter.categories.${it.category}`, { defaultValue: it.category })}</Badge>
                    <span className="text-xs text-muted-foreground tabular-nums">{t('resourceCenter.analytics.score', { defaultValue: 'score' })} {it.usage_score}</span>
                  </li>
                ))}
              </ol>
            </div>

            <p className="text-xs text-muted-foreground italic">
              {t('resourceCenter.analytics.generated', { defaultValue: 'Generated {{date}} · usage score weighting: views ×1, previews ×1, downloads ×2, imports ×3.', date: new Date(data.generated_at).toLocaleString(getUiLocale()) })}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Compact strip that lives at the top of the Resource Center grid.
export function TopFiveStrip({ onOpenAnalytics, onOpenResource }) {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const { data } = useResourceAnalytics(true);
  if (!data || !(data.top_5_this_month || []).length) return null;
  return (
    <Card className="p-4" data-testid="resource-top-5-strip">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5 text-primary" />
          {t('resourceCenter.analytics.topFiveStrip', { defaultValue: 'Top 5 most-used resources this month' })}
        </div>
        <Button
          size="sm" variant="ghost" className="h-7 gap-1 text-primary"
          onClick={onOpenAnalytics}
          data-testid="resource-top-5-open-analytics"
        >
          <BarChart3 className="h-3.5 w-3.5" /> {t('resourceCenter.analytics.viewAll', { defaultValue: 'View all analytics' })}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="resource-top-5-items">
        {data.top_5_this_month.map((it, idx) => (
          <button
            key={it.id}
            type="button"
            className="group flex items-center gap-2 rounded-full border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors px-3 py-1.5"
            onClick={() => onOpenResource?.(it)}
            data-testid={`resource-top-5-item-${idx}`}
          >
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary/10 text-primary text-xs font-bold">{idx + 1}</span>
            <span className="text-xs font-medium max-w-[180px] truncate group-hover:text-primary">{tData(it.title)}</span>
            <span className="text-xs text-muted-foreground">· {t(`resourceCenter.categories.${it.category}`, { defaultValue: it.category })}</span>
          </button>
        ))}
      </div>
    </Card>
  );
}
